import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { formatDateTimeKST, formatDecimal } from '../utils/dateUtils';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  keyframes,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Divider,
  Skeleton,
} from '@mui/material';
import { Refresh, TrendingUp, TrendingDown, Notifications, NotificationsOff, Close, Delete, CompareArrows, VerifiedUser, CheckCircle, Error, Warning } from '@mui/icons-material';
import { fetchTradeHistory, fetchTradeStatistics, fetchTradeExchangePairs } from '../store/tradeHistorySlice';
import { fetchPnlHistory, fetchPnlSummary, deletePnlHistory, fetchRelatedTrades, clearRelatedTrades, clearDeleteError, fetchPnlDailyChart, fetchPnlExchangePairs } from '../store/pnlHistorySlice';
import { validateTrades, openValidationDialog, closeValidationDialog, clearValidationResult } from '../store/validationSlice';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine, Brush } from 'recharts';
import { CryptoIcon, ExchangeIcon } from '../components/IconDisplay';
import wsService from '../services/websocket';
import { PAGE_REFRESH_MS } from '../config/intervals';

// Animation for flashing rows
const flashAnimation = keyframes`
  0% { background-color: transparent; }
  25% { background-color: rgba(76, 175, 80, 0.3); }
  75% { background-color: rgba(76, 175, 80, 0.3); }
  100% { background-color: transparent; }
`;

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function StatCard({ title, value, color }) {
  return (
    <Card>
      <CardContent>
        <Typography color="textSecondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function Trades() {
  const dispatch = useDispatch();
  const location = useLocation();
  // Check if we navigated here with a trigger filter from another page
  const initialTrigger = location.state?.selectedTrigger || null;
  const initialTab = location.state?.tabValue !== undefined ? location.state.tabValue : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [recentExecutions, setRecentExecutions] = useState(new Set());
  const [wsConnected, setWsConnected] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [selectedTrigger, setSelectedTrigger] = useState(initialTrigger); // Selected trigger for filtering trade history
  const [page, setPage] = useState(0); // Trade History pagination page (0-indexed)
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pnlPage, setPnlPage] = useState(0); // PNL History pagination page (0-indexed)
  const [pnlRowsPerPage, setPnlRowsPerPage] = useState(100);
  const [selectedExchangePair, setSelectedExchangePair] = useState(''); // Exchange pair filter
  const flashTimeoutRefs = useRef(new Map());
  const tradeRefreshTimerRef = useRef(null);
  const notificationsEnabledRef = useRef(notificationsEnabled);
  const latestQueryRef = useRef({
    page,
    rowsPerPage,
    pnlPage,
    pnlRowsPerPage,
    selectedTriggerUuid: selectedTrigger?.uuid || null,
    selectedExchangePair,
  });

  const { trades: tradeHistory, count: tradeCount, statistics: tradeStats, loading: tradesLoading, exchangePairs: tradeExchangePairs } = useSelector((state) => state.tradeHistory);
  const { pnlHistory, count: pnlCount, summary: pnlSummary, loading: pnlLoading, deleting, deleteError, relatedTrades, loadingRelatedTrades, chartData, chartLoading, exchangePairs: pnlExchangePairs } = useSelector((state) => state.pnlHistory);
  const { validationResult, validating: validationLoading, validationError, validationDialogOpen } = useSelector((state) => state.validation);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pnlToDelete, setPnlToDelete] = useState(null);

  // Fetch exchange pairs on mount
  useEffect(() => {
    dispatch(fetchTradeExchangePairs());
    dispatch(fetchPnlExchangePairs());
  }, [dispatch]);

  // Fetch trade history with optional trigger filter and pagination
  useEffect(() => {
    const params = { page: page + 1, page_size: rowsPerPage }; // DRF uses 1-indexed pages
    if (selectedTrigger) {
      params.trigger = selectedTrigger.uuid;
    }
    if (selectedExchangePair) {
      params.exchange_pair = selectedExchangePair;
    }
    dispatch(fetchTradeHistory(params));
  }, [dispatch, selectedTrigger, page, rowsPerPage, selectedExchangePair]);

  // Fetch PNL history with pagination and optional trigger filter
  useEffect(() => {
    const params = { page: pnlPage + 1, page_size: pnlRowsPerPage };
    if (selectedTrigger?.uuid) {
      params.trigger = selectedTrigger.uuid;
    }
    if (selectedExchangePair) {
      params.exchange_pair = selectedExchangePair;
    }
    dispatch(fetchPnlHistory(params));
  }, [dispatch, selectedTrigger, pnlPage, pnlRowsPerPage, selectedExchangePair]);

  // Fetch chart data when exchange pair filter changes
  useEffect(() => {
    const chartParams = selectedExchangePair ? { exchange_pair: selectedExchangePair } : {};
    dispatch(fetchPnlDailyChart(chartParams));
  }, [dispatch, selectedExchangePair]);

  useEffect(() => {
    notificationsEnabledRef.current = notificationsEnabled;
  }, [notificationsEnabled]);

  useEffect(() => {
    latestQueryRef.current = {
      page,
      rowsPerPage,
      pnlPage,
      pnlRowsPerPage,
      selectedTriggerUuid: selectedTrigger?.uuid || null,
      selectedExchangePair,
    };
  }, [page, rowsPerPage, pnlPage, pnlRowsPerPage, selectedTrigger, selectedExchangePair]);

  const refreshVisibleData = useCallback((options = {}) => {
    const {
      page: currentPage,
      rowsPerPage: currentRowsPerPage,
      pnlPage: currentPnlPage,
      pnlRowsPerPage: currentPnlRowsPerPage,
      selectedTriggerUuid,
      selectedExchangePair: currentExchangePair,
    } = latestQueryRef.current;

    const tradeParams = { page: currentPage + 1, page_size: currentRowsPerPage };
    if (selectedTriggerUuid) {
      tradeParams.trigger = selectedTriggerUuid;
    }
    if (currentExchangePair) {
      tradeParams.exchange_pair = currentExchangePair;
    }

    const pnlParams = { page: currentPnlPage + 1, page_size: currentPnlRowsPerPage };
    if (selectedTriggerUuid) {
      pnlParams.trigger = selectedTriggerUuid;
    }
    if (currentExchangePair) {
      pnlParams.exchange_pair = currentExchangePair;
    }

    dispatch(fetchTradeHistory(tradeParams));
    dispatch(fetchTradeStatistics());
    dispatch(fetchPnlHistory(pnlParams));
    dispatch(fetchPnlSummary());

    if (options.includeChart) {
      const chartParams = currentExchangePair ? { exchange_pair: currentExchangePair } : {};
      dispatch(fetchPnlDailyChart(chartParams));
    }
  }, [dispatch]);

  const scheduleTradeExecutionRefresh = useCallback(() => {
    if (tradeRefreshTimerRef.current) {
      return;
    }

    tradeRefreshTimerRef.current = setTimeout(() => {
      tradeRefreshTimerRef.current = null;
      refreshVisibleData();
    }, 500);
  }, [refreshVisibleData]);

  const handleTradeExecution = useCallback((execution) => {
    if (notificationsEnabledRef.current) {
      // Add to recent executions for flashing effect
      const executionKey = `${execution.trading_pair_id}-${execution.timestamp}`;
      setRecentExecutions(prev => new Set([...prev, executionKey]));

      // Remove after animation completes (2 seconds)
      const timeout = setTimeout(() => {
        setRecentExecutions(prev => {
          const newSet = new Set(prev);
          newSet.delete(executionKey);
          return newSet;
        });
        flashTimeoutRefs.current.delete(executionKey);
      }, 2000);

      flashTimeoutRefs.current.set(executionKey, timeout);
    }

    // Keep data fresh even when visual notifications are disabled. Multiple
    // execution messages in a burst are coalesced into one REST refresh, and
    // the refresh reads the latest filter/page values from latestQueryRef.
    scheduleTradeExecutionRefresh();
  }, [scheduleTradeExecutionRefresh]);

  useEffect(() => {
    const flashTimeouts = flashTimeoutRefs.current;

    return () => {
      flashTimeouts.forEach(timeout => clearTimeout(timeout));
      flashTimeouts.clear();

      if (tradeRefreshTimerRef.current) {
        clearTimeout(tradeRefreshTimerRef.current);
        tradeRefreshTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    dispatch(fetchTradeStatistics());
    dispatch(fetchPnlSummary());

    // Refresh statistics and summary every 30 seconds
    const interval = setInterval(() => {
      dispatch(fetchTradeStatistics());
      dispatch(fetchPnlSummary());
    }, PAGE_REFRESH_MS);

    // Set up WebSocket connection
    wsService.connect();
    
    // Listen for connection status
    const removeConnectionListener = wsService.addEventListener('connection', (data) => {
      setWsConnected(data.status === 'connected');
    });

    // Check initial connection state (in case WebSocket is already connected)
    setWsConnected(wsService.isConnected());

    // Listen for trade executions
    const removeTradeListener = wsService.addEventListener('trade_execution', (data) => {
      handleTradeExecution(data);
    });
    
    // Start ping to keep connection alive
    wsService.startPing();

    return () => {
      clearInterval(interval);
      removeConnectionListener();
      removeTradeListener();
    };
  }, [dispatch, handleTradeExecution]);

  const handleRefresh = () => {
    refreshVisibleData({ includeChart: true });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Keep trigger filter when switching tabs, just reset pagination
    if (newValue === 1) {
      setPage(0);
    } else {
      setPnlPage(0);
    }
  };

  // Handle clicking on a PnL History row to filter Trade History
  const handlePnlRowClick = (pnl) => {
    if (pnl.trigger) {
      setSelectedTrigger({
        uuid: pnl.trigger,
        symbol: pnl.trading_pair_symbol,
        display: pnl.trigger_display,
        // Store PnL data for summary table
        pnlData: {
          // Target exchange info
          target_exchange_name: pnl.target_exchange_name,
          target_market_type: pnl.target_market_type,
          target_quote_asset: pnl.target_quote_asset,
          // Origin exchange info
          origin_exchange_name: pnl.origin_exchange_name,
          origin_market_type: pnl.origin_market_type,
          origin_quote_asset: pnl.origin_quote_asset,
          // LS trades (Target Long / Origin Short)
          target_long_executed_qty: pnl.target_long_executed_qty,
          target_long_executed_avg_price: pnl.target_long_executed_avg_price,
          target_long_executed_avg_fee: pnl.target_long_executed_avg_fee,
          origin_short_executed_qty: pnl.origin_short_executed_qty,
          origin_short_executed_avg_price: pnl.origin_short_executed_avg_price,
          origin_short_executed_avg_fee: pnl.origin_short_executed_avg_fee,
          // SL trades (Target Short / Origin Long)
          target_short_executed_qty: pnl.target_short_executed_qty,
          target_short_executed_avg_price: pnl.target_short_executed_avg_price,
          target_short_executed_avg_fee: pnl.target_short_executed_avg_fee,
          origin_long_executed_qty: pnl.origin_long_executed_qty,
          origin_long_executed_avg_price: pnl.origin_long_executed_avg_price,
          origin_long_executed_avg_fee: pnl.origin_long_executed_avg_fee,
        }
      });
      setPage(0); // Reset to first page when selecting a trigger
      setTabValue(1); // Switch to Trade History tab
    }
  };

  // Clear trigger filter
  const handleClearTriggerFilter = () => {
    setSelectedTrigger(null);
    setPage(0); // Reset to first page when clearing filter
    setPnlPage(0);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // PNL History pagination handlers
  const handleChangePnlPage = (event, newPage) => {
    setPnlPage(newPage);
  };

  const handleChangePnlRowsPerPage = (event) => {
    setPnlRowsPerPage(parseInt(event.target.value, 10));
    setPnlPage(0);
  };

  // Delete PNL History handlers
  const handleDeleteClick = (event, pnl) => {
    event.stopPropagation(); // Prevent row click handler
    setPnlToDelete(pnl);
    setDeleteDialogOpen(true);
    // Fetch related trades count
    dispatch(fetchRelatedTrades(pnl.uuid));
  };

  const handleDeleteConfirm = async () => {
    if (pnlToDelete) {
      await dispatch(deletePnlHistory({ pnlUuid: pnlToDelete.uuid, deleteTrades: true }));
      // Refresh the PNL history and summary
      dispatch(fetchPnlHistory({ page: pnlPage + 1, page_size: pnlRowsPerPage }));
      dispatch(fetchPnlSummary());
      dispatch(fetchTradeStatistics());
      handleDeleteDialogClose();
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setPnlToDelete(null);
    dispatch(clearRelatedTrades());
    dispatch(clearDeleteError());
  };

  // Validation handlers
  const handleValidateTrades = () => {
    if (selectedTrigger?.uuid) {
      dispatch(openValidationDialog());
      dispatch(validateTrades({ triggerUuid: selectedTrigger.uuid }));
    }
  };

  const handleValidationDialogClose = () => {
    dispatch(closeValidationDialog());
    dispatch(clearValidationResult());
  };

  // Total PnL comes from backend summary (pnlSummary.total_pnl)
  // NOT calculated from paginated pnlHistory data
  const totalPnl = pnlSummary?.total_pnl || 0;

  // Total Volume (USDT) now comes from backend trade statistics
  // It calculates sum of (price * qty) for all trades, converting KRW to USD

  // Trade history is now server-side filtered when selectedTrigger is set
  const filteredTradeHistory = tradeHistory || [];

  const formatNumber = (num) => {
    const value = Number(num || 0);
    if (isNaN(value)) return '0';
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    });
  };

  const getSideColor = (side) => {
    return side === 'buy' ? 'primary' : 'secondary';
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Trading History & PnL</Typography>
            <Chip
              size="small"
              label={wsConnected ? 'Live' : 'Offline'}
              color={wsConnected ? 'success' : 'default'}
              variant={wsConnected ? 'filled' : 'outlined'}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}>
              <IconButton
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                color={notificationsEnabled ? 'primary' : 'default'}
                aria-label={notificationsEnabled ? 'Disable trade notifications' : 'Enable trade notifications'}
                aria-pressed={notificationsEnabled}
              >
                {notificationsEnabled ? <Notifications /> : <NotificationsOff />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={tradesLoading || pnlLoading}
                aria-label="Refresh trades"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Trades"
            value={tradeStats?.total_trades || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Volume (USDT)"
            value={(tradeStats?.total_volume_usdt || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total PnL (USDT)"
            value={parseFloat(parseFloat(totalPnl).toFixed(1)).toLocaleString()}
            color={Number(totalPnl) >= 0 ? 'success.main' : 'error.main'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Premium"
            value={`${formatDecimal(pnlSummary?.avg_premium || 0)}%`}
          />
        </Grid>
      </Grid>

      <Paper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="PnL History" />
            <Tab label="Trade History" />
          </Tabs>
          <Box sx={{ pr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel id="exchange-pair-filter-label">Exchange Pair Filter</InputLabel>
              <Select
                labelId="exchange-pair-filter-label"
                id="exchange-pair-filter"
                value={selectedExchangePair}
                label="Exchange Pair Filter"
                onChange={(e) => {
                  setSelectedExchangePair(e.target.value);
                  setPage(0);
                  setPnlPage(0);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em style={{ color: '#999' }}>All Exchange Pairs</em>;
                  const pairs = tabValue === 0 ? pnlExchangePairs : tradeExchangePairs;
                  const pair = pairs?.find(p => p.id === selected);
                  if (!pair) return selected;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExchangeIcon exchangeName={pair.target_exchange} size={18} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{pair.target_market_type}</Typography>
                      <CompareArrows sx={{ fontSize: 16, color: 'text.secondary', mx: 0.5 }} />
                      <ExchangeIcon exchangeName={pair.origin_exchange} size={18} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{pair.origin_market_type}</Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <em style={{ color: '#666' }}>All Exchange Pairs</em>
                  </Box>
                </MenuItem>
                {(tabValue === 0 ? pnlExchangePairs : tradeExchangePairs)?.map((pair) => (
                  <MenuItem key={pair.id} value={pair.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                      <ExchangeIcon exchangeName={pair.target_exchange} size={20} />
                      <Typography variant="body2" sx={{ minWidth: 50 }}>{pair.target_market_type}</Typography>
                      <CompareArrows sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <ExchangeIcon exchangeName={pair.origin_exchange} size={20} />
                      <Typography variant="body2" sx={{ minWidth: 50 }}>{pair.origin_market_type}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedExchangePair && (
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedExchangePair('');
                  setPage(0);
                  setPnlPage(0);
                }}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
                aria-label="Clear exchange pair filter"
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <TabPanel value={tabValue} index={1}>
          {/* Filter indicator */}
          {selectedTrigger && (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing trades for trigger:
                </Typography>
                <Chip
                  label={selectedTrigger.uuid}
                  onDelete={handleClearTriggerFilter}
                  deleteIcon={<Close />}
                  color="primary"
                  size="small"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
                <Button size="small" onClick={handleClearTriggerFilter}>
                  Show All
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={validationLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUser />}
                  onClick={handleValidateTrades}
                  disabled={validationLoading}
                  sx={{ ml: 1 }}
                >
                  {validationLoading ? 'Validating...' : 'Validate Trades'}
                </Button>
              </Box>

              {/* Trade Summary Table */}
              {selectedTrigger.pnlData && (
                <Paper sx={{ mb: 3, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    Trade Summary
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Target Exchange Summary */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ExchangeIcon exchangeName={selectedTrigger.pnlData.target_exchange_name} size={18} />
                        <Typography variant="body2" fontWeight="medium">
                          {selectedTrigger.pnlData.target_exchange_name} ({selectedTrigger.pnlData.target_market_type}) - {selectedTrigger.pnlData.target_quote_asset}
                        </Typography>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Trade Type</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Qty</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Total Value</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Fee</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ py: 1 }}>
                                <Chip label="BUY (LS)" size="small" color="primary" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.target_long_executed_qty || 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber((selectedTrigger.pnlData.target_long_executed_qty || 0) * (selectedTrigger.pnlData.target_long_executed_avg_price || 0))} {selectedTrigger.pnlData.target_quote_asset}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.target_long_executed_avg_fee || 0)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ py: 1 }}>
                                <Chip label="SELL (SL)" size="small" color="secondary" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.target_short_executed_qty || 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber((selectedTrigger.pnlData.target_short_executed_qty || 0) * (selectedTrigger.pnlData.target_short_executed_avg_price || 0))} {selectedTrigger.pnlData.target_quote_asset}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.target_short_executed_avg_fee || 0)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Origin Exchange Summary */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ExchangeIcon exchangeName={selectedTrigger.pnlData.origin_exchange_name} size={18} />
                        <Typography variant="body2" fontWeight="medium">
                          {selectedTrigger.pnlData.origin_exchange_name} ({selectedTrigger.pnlData.origin_market_type}) - {selectedTrigger.pnlData.origin_quote_asset}
                        </Typography>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Trade Type</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Qty</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Total Value</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', py: 1 }}>Fee</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ py: 1 }}>
                                <Chip label="SHORT (LS)" size="small" color="secondary" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.origin_short_executed_qty || 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber((selectedTrigger.pnlData.origin_short_executed_qty || 0) * (selectedTrigger.pnlData.origin_short_executed_avg_price || 0))} {selectedTrigger.pnlData.origin_quote_asset}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.origin_short_executed_avg_fee || 0)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ py: 1 }}>
                                <Chip label="LONG (SL)" size="small" color="primary" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.origin_long_executed_qty || 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber((selectedTrigger.pnlData.origin_long_executed_qty || 0) * (selectedTrigger.pnlData.origin_long_executed_avg_price || 0))} {selectedTrigger.pnlData.origin_quote_asset}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                {formatNumber(selectedTrigger.pnlData.origin_long_executed_avg_fee || 0)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </>
          )}
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Trading Pair</TableCell>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Leverage</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>Order Type</TableCell>
                  <TableCell>Order ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tradesLoading && (!filteredTradeHistory || filteredTradeHistory.length === 0) ? (
                  // Skeleton loaders while initial data is loading
                  Array(5).fill(0).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={40} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    </TableRow>
                  ))
                ) : (!filteredTradeHistory || filteredTradeHistory.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {selectedTrigger ? 'No trades found for this trigger' : 'No trades found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTradeHistory.map((trade) => {
                    // Check if this trade is from a recent execution
                    const isRecentExecution = Array.from(recentExecutions).some(key =>
                      key.startsWith(`${trade.trading_pair_id}-`)
                    );

                    return (
                    <TableRow
                      key={trade.uuid}
                      sx={{
                        animation: isRecentExecution ? `${flashAnimation} 2s ease-in-out` : 'none',
                      }}
                    >
                      <TableCell>
                        {formatDateTimeKST(trade.created_at)}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CryptoIcon symbol={trade.symbol || trade.trading_pair_symbol} size={20} />
                            <Typography variant="body2" fontWeight="medium">
                              {trade.trading_pair_symbol || 'N/A'}
                            </Typography>
                          </Box>
                          {trade.target_exchange_name && trade.origin_exchange_name && (
                            <>
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ pl: 0.5 }}>
                                <ExchangeIcon exchangeName={trade.target_exchange_name} size={14} />
                                <Typography variant="caption" color="text.secondary">
                                  Target: {trade.target_exchange_name} - {trade.target_market_name || 'N/A'} ({trade.target_quote_asset || 'N/A'})
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ pl: 0.5 }}>
                                <ExchangeIcon exchangeName={trade.origin_exchange_name} size={14} />
                                <Typography variant="caption" color="text.secondary">
                                  Origin: {trade.origin_exchange_name} - {trade.origin_market_name || 'N/A'} ({trade.origin_quote_asset || 'N/A'})
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ExchangeIcon exchangeName={trade.exchange_name} size={20} />
                          <Box>
                            <Typography variant="body2">{trade.exchange_name || 'N/A'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {trade.market_name || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={trade.side.toUpperCase()}
                          color={getSideColor(trade.side)}
                          size="small"
                          icon={trade.side === 'buy' ? <TrendingUp /> : <TrendingDown />}
                        />
                      </TableCell>
                      <TableCell>{formatNumber(trade.price)}</TableCell>
                      <TableCell>{formatNumber(trade.qty)}</TableCell>
                      <TableCell>
                        {trade.leverage ? `${trade.leverage}x` : '-'}
                      </TableCell>
                      <TableCell>{formatNumber(trade.fee)}</TableCell>
                      <TableCell>{trade.order_type}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {trade.order_id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={tradeCount || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={0}>
          {/* Trigger Filter Indicator for PnL History */}
          {selectedTrigger && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing PnL for trigger:
              </Typography>
              <Chip
                label={`${selectedTrigger.trading_pair_symbol || selectedTrigger.symbol || ''} (${selectedTrigger.uuid?.substring(0, 8)}...)`}
                onDelete={handleClearTriggerFilter}
                deleteIcon={<Close />}
                color="primary"
                size="small"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
              <Button size="small" onClick={handleClearTriggerFilter}>
                Show All
              </Button>
            </Box>
          )}

          {/* PnL Chart */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              PnL Trend
            </Typography>
            {chartLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : chartData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">No PnL data available</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      // Format date as MM/DD
                      const parts = value.split('-');
                      return `${parts[1]}/${parts[2]}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value, name) => [
                      `${value >= 0 ? '+' : ''}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`,
                      name
                    ]}
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                  <Line
                    type="monotone"
                    dataKey="dailyPnl"
                    name="Daily PnL"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#8884d8' }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulativePnl"
                    name="Cumulative PnL"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#82ca9d' }}
                    activeDot={{ r: 6 }}
                  />
                  <Brush
                    dataKey="date"
                    height={30}
                    stroke="#8884d8"
                    fill="#1e1e1e"
                    tickFormatter={(value) => {
                      const parts = value.split('-');
                      return `${parts[1]}/${parts[2]}`;
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Trading Pair</TableCell>
                  <TableCell>Capital (Target / Origin)</TableCell>
                  <TableCell>LS Premium</TableCell>
                  <TableCell>SL Premium</TableCell>
                  <TableCell>Realized PnL (USDT)</TableCell>
                  <TableCell>Trigger Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!pnlHistory || pnlHistory.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No PnL history found
                    </TableCell>
                  </TableRow>
                ) : (
                  pnlHistory.map((pnl) => {
                    const realizedPnl = pnl.realized_pnl;

                    return (
                      <TableRow
                        key={pnl.uuid}
                        onClick={() => handlePnlRowClick(pnl)}
                        sx={{
                          cursor: pnl.trigger ? 'pointer' : 'default',
                          '&:hover': pnl.trigger ? { bgcolor: 'action.hover' } : {}
                        }}
                      >
                        <TableCell>
                          {formatDateTimeKST(pnl.created_at)}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CryptoIcon symbol={pnl.trading_pair_symbol} size={20} />
                              <Typography variant="body2" fontWeight="medium">
                                {pnl.trading_pair_symbol || 'N/A'}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ pl: 0.5 }}>
                              <ExchangeIcon exchangeName={pnl.target_exchange_name} size={14} />
                              <Typography variant="caption" color="text.secondary">
                                Target: {pnl.target_exchange_name || 'N/A'} - {pnl.target_market_type || 'N/A'} ({pnl.target_quote_asset || 'N/A'})
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ pl: 0.5 }}>
                              <ExchangeIcon exchangeName={pnl.origin_exchange_name} size={14} />
                              <Typography variant="caption" color="text.secondary">
                                Origin: {pnl.origin_exchange_name || 'N/A'} - {pnl.origin_market_type || 'N/A'} ({pnl.origin_quote_asset || 'N/A'})
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {formatNumber(pnl.target_entered_capital)} {pnl.target_quote_asset || ''}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {formatNumber(pnl.origin_entered_capital)} {pnl.origin_quote_asset || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: pnl.avg_LS_premium ? 'success.main' : 'text.secondary',
                              fontWeight: pnl.avg_LS_premium ? 'medium' : 'normal'
                            }}
                          >
                            {pnl.avg_LS_premium ? `${formatDecimal(pnl.avg_LS_premium)}%` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: pnl.avg_SL_premium ? 'error.main' : 'text.secondary',
                              fontWeight: pnl.avg_SL_premium ? 'medium' : 'normal'
                            }}
                          >
                            {pnl.avg_SL_premium ? `${formatDecimal(pnl.avg_SL_premium)}%` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: realizedPnl > 0 ? 'success.main' : realizedPnl < 0 ? 'error.main' : 'text.secondary',
                              fontWeight: 'bold',
                              fontSize: '0.9rem'
                            }}
                          >
                            {realizedPnl !== null && realizedPnl !== undefined
                              ? `${realizedPnl > 0 ? '+' : ''}${realizedPnl.toFixed(2)}`
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={pnl.trigger_deleted ? 'Deleted' : 'Active'}
                            color={pnl.trigger_deleted ? 'error' : 'success'}
                            size="small"
                            variant={pnl.trigger_deleted ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete PnL record and related trades">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(e, pnl)}
                              aria-label="Delete PnL record"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pnlCount || 0}
            page={pnlPage}
            onPageChange={handleChangePnlPage}
            rowsPerPage={pnlRowsPerPage}
            onRowsPerPageChange={handleChangePnlRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </TabPanel>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Record</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete this record?
          </DialogContentText>
          {loadingRelatedTrades ? (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking related trades...
              </Typography>
            </Box>
          ) : relatedTrades && relatedTrades.trade_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This will also delete <strong>{relatedTrades.trade_count}</strong> related trade history record{relatedTrades.trade_count !== 1 ? 's' : ''}.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting || loadingRelatedTrades}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Results Dialog */}
      <Dialog
        open={validationDialogOpen}
        onClose={handleValidationDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VerifiedUser color="primary" />
            Trade Validation Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {validationLoading && (
            <Box sx={{ py: 4 }}>
              <LinearProgress sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="primary">
                    Fetching USD/KRW exchange rate from server...
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="primary">
                    Validating target exchange orders against API...
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="primary">
                    Validating origin exchange orders against API...
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="primary">
                    Calculating and comparing PnL...
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 3 }}>
                This may take 30-60 seconds depending on the number of trades.
              </Typography>
            </Box>
          )}

          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {validationResult && !validationLoading && (
            <Box>
              {/* Summary Section */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: validationResult.summary?.match_rate === 100 ? 'success.dark' : 'warning.dark' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {validationResult.summary?.match_rate === 100 ? (
                      <CheckCircle color="inherit" />
                    ) : (
                      <Warning color="inherit" />
                    )}
                    <Typography variant="h6">
                      {validationResult.summary?.match_rate === 100 ? 'All Trades Verified!' : 'Validation Complete with Issues'}
                    </Typography>
                  </Box>
                  <Chip
                    label={`USD/KRW: ${validationResult.usd_krw_rate ? validationResult.usd_krw_rate.toFixed(2) : 'N/A'}`}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Total Trades</Typography>
                    <Typography variant="h5">{validationResult.summary?.total_trades || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Matched</Typography>
                    <Typography variant="h5" sx={{ color: 'success.light' }}>
                      {validationResult.summary?.matched || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Mismatched</Typography>
                    <Typography variant="h5" sx={{ color: validationResult.summary?.mismatched > 0 ? 'error.light' : 'inherit' }}>
                      {validationResult.summary?.mismatched || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Match Rate</Typography>
                    <Typography variant="h5">
                      {validationResult.summary?.match_rate?.toFixed(1) || 0}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Exchange Breakdown */}
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Validation by Exchange
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {validationResult.target_exchange && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ExchangeIcon exchangeName={validationResult.target_exchange.exchange_name} size={24} />
                        <Typography variant="subtitle2">{validationResult.target_exchange.exchange_name} (Target)</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Matched: {validationResult.target_exchange.verified} / {validationResult.target_exchange.total}
                        </Typography>
                        {validationResult.target_exchange.mismatched > 0 && (
                          <Typography variant="body2" color="error">
                            Mismatched: {validationResult.target_exchange.mismatched}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                )}
                {validationResult.origin_exchange && (
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ExchangeIcon exchangeName={validationResult.origin_exchange.exchange_name} size={24} />
                        <Typography variant="subtitle2">{validationResult.origin_exchange.exchange_name} (Origin)</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Matched: {validationResult.origin_exchange.verified} / {validationResult.origin_exchange.total}
                        </Typography>
                        {validationResult.origin_exchange.mismatched > 0 && (
                          <Typography variant="body2" color="error">
                            Mismatched: {validationResult.origin_exchange.mismatched}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* PnL Comparison */}
              {validationResult.pnl_comparison && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    PnL Comparison
                  </Typography>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Calculated PnL (USDT)</Typography>
                        <Typography variant="h6" sx={{ color: validationResult.pnl_comparison.calculated_pnl >= 0 ? 'success.main' : 'error.main' }}>
                          {validationResult.pnl_comparison.calculated_pnl?.toFixed(4) || '0.0000'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Recorded PnL (USDT)</Typography>
                        <Typography variant="h6" sx={{ color: validationResult.pnl_comparison.recorded_pnl >= 0 ? 'success.main' : 'error.main' }}>
                          {validationResult.pnl_comparison.recorded_pnl?.toFixed(4) || '0.0000'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Difference</Typography>
                        <Typography variant="h6" sx={{
                          color: Math.abs(validationResult.pnl_comparison.difference || 0) < 0.01 ? 'success.main' : 'warning.main'
                        }}>
                          {validationResult.pnl_comparison.difference?.toFixed(4) || '0.0000'}
                          {Math.abs(validationResult.pnl_comparison.difference || 0) < 0.01 && (
                            <CheckCircle sx={{ ml: 1, fontSize: 18, color: 'success.main' }} />
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                    {validationResult.pnl_comparison.match !== undefined && (
                      <Box mt={2}>
                        <Chip
                          icon={validationResult.pnl_comparison.match ? <CheckCircle /> : <Error />}
                          label={validationResult.pnl_comparison.match ? 'PnL Matches' : 'PnL Mismatch'}
                          color={validationResult.pnl_comparison.match ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    )}
                  </Paper>
                </>
              )}

              {/* Mismatched Trades Details */}
              {validationResult.mismatched_details && validationResult.mismatched_details.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    Mismatched Trade Details
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order ID</TableCell>
                          <TableCell>Exchange</TableCell>
                          <TableCell>Issue</TableCell>
                          <TableCell>DB Value</TableCell>
                          <TableCell>API Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResult.mismatched_details.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {item.order_id}
                            </TableCell>
                            <TableCell>{item.exchange}</TableCell>
                            <TableCell>{item.issue}</TableCell>
                            <TableCell>{item.db_value}</TableCell>
                            <TableCell>{item.api_value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleValidationDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Trades;
